import { cn } from '../../../utils/cn';
import { exhaustiveCheck } from '../../../utils/exhaustive-check';
import { getInitials } from './get-initials';

type AvatarSize = '2xs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';

type AvatarProps = {
  avatarUrl?: string | null;
  name: string;
  size?: AvatarSize;
  className?: string;
};

export const Avatar = ({ avatarUrl, name, size, className }: AvatarProps) => {
  if (!avatarUrl) {
    return <InitialsAvatar name={name} size={size} className={className} />;
  }

  return (
    <div className={cn('flex-shrink-0 overflow-hidden rounded-full bg-white', getAvatarSizeClasses(size), className)}>
      <img src={avatarUrl} alt="" className="h-full w-full object-cover" />
    </div>
  );
};

type InitialsAvatarProps = {
  name: string;
  size?: AvatarSize;
  className?: string;
};

const InitialsAvatar = ({ name, size = 'md', className }: InitialsAvatarProps) => {
  const initials = getInitials(name);

  if (!initials) {
    return null;
  }

  return (
    <span
      className={cn(
        'flex items-center justify-center rounded-full border border-seapoint-gray-medium bg-white text-seapoint-gray-very-dark',
        getSizeClasses(size),
        className,
      )}
      aria-hidden="true"
    >
      {initials}
    </span>
  );
};

const getAvatarSizeClasses = (size?: AvatarSize) => {
  const sizeClasses = {
    '2xs': 'w-4 h-4', // 16px
    xs: 'w-6 h-6', // 24px
    sm: 'w-8 h-8', // 32px
    md: 'w-10 h-10', // 40px
    lg: 'w-12 h-12', // 48px
    xl: 'w-[50px] h-[50px]', // 50px
    '2xl': 'w-16 h-16', // 64px
  };
  return sizeClasses[size ?? 'md'];
};

const getSizeClasses = (size: InitialsAvatarProps['size']) => {
  if (!size) {
    return '';
  }

  switch (size) {
    case '2xs':
      return `${getAvatarSizeClasses(size)} text-[8px] font-light`;
    case 'xs':
      return `${getAvatarSizeClasses(size)} text-[8px] font-normal`;
    case 'sm':
      return `${getAvatarSizeClasses(size)} text-sm font-normal`;
    case 'md':
      return `${getAvatarSizeClasses(size)} text-base font-bold`;
    case 'lg':
      return `${getAvatarSizeClasses(size)} text-lg font-bold`;
    case 'xl':
      return `${getAvatarSizeClasses(size)} text-xl font-bold`;
    case '2xl':
      return `${getAvatarSizeClasses(size)} text-3xl font-bold`;
    default:
      return exhaustiveCheck(size);
  }
};
